import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, PencilLine, Trash2, ClipboardList, ListChecks, Kanban, Activity, LayoutDashboard, DollarSign, CalendarClock, Users2, ChevronRight, Check, Paperclip, Flag, Bug } from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { PROJECT_STATUS_COLORS, PRIORITY_COLORS } from "../components/project/projectUtils";
import ProjectForm from "../components/project/ProjectForm";
import RequirementsPanel from "../components/project/RequirementsPanel";
import TasksPanel from "../components/project/TasksPanel";
import KanbanBoard from "../components/project/KanbanBoard";
import ActivityLog from "../components/project/ActivityLog";
import MilestonesPanel from "../components/project/MilestonesPanel";
import BugsPanel from "../components/project/BugsPanel";
import StandupPanel from "../components/project/StandupPanel";
import ProjectCostPanel from "../components/project/ProjectCostPanel";
import WorkflowEditor from "../components/project/WorkflowEditor";
import { fileUrl } from "../lib/api";
import { formatMoney } from "../lib/currency";
import { canSeeCosts } from "../lib/services";
import { useConfirm } from "../components/ConfirmProvider";

export default function ProjectDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [workflowOpen, setWorkflowOpen] = useState(false);
  const [activityKey, setActivityKey] = useState(0);

  const load = async () => {
    try {
      const [d, u, c, m] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get("/users").catch(() => ({ data: [] })),
        api.get("/clients").catch(() => ({ data: [] })),
        api.get(`/projects/${id}/milestones`).catch(() => ({ data: [] })),
      ]);
      setData(d.data);
      setUsers(u.data);
      setClients(c.data);
      setMilestones(m.data);
      setActivityKey(k => k + 1);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      if (e.response?.status === 404) navigate("/projects");
    }
  };

  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="p-8 text-neutral-500">Loading…</div>;

  const { project, requirements, tasks, stats, client } = data;
  const canEdit = ["admin", "ceo", "coo", "manager", "project_manager"].includes(user?.role);
  const isAdmin = ["admin", "ceo", "coo"].includes(user?.role);
  const isClient = user?.role === "client";
  const canEditTasks = canEdit || ["developer", "tester"].includes(user?.role);
  const nameOf = (uid) => users.find(u => u.id === uid)?.name || "—";

  const remove = async () => {
    if (!(await confirm({ title: "Are you sure you want to delete this project?", description: "All its requirements, tasks, milestones and bugs will be removed." }))) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      navigate("/projects");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const advanceStage = async (stage) => {
    try {
      await api.put(`/projects/${id}`, { current_stage: stage });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const stages = project.workflow_stages || [];
  const currentIdx = stages.indexOf(project.current_stage);
  const totalTasks = tasks.length;
  const doneTasks = tasks.filter(t => t.status === "Completed").length;
  const pct = totalTasks ? Math.round(doneTasks / totalTasks * 100) : 0;

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1600px]">
      <Link to="/projects" className="inline-flex items-center gap-2 text-xs font-mono uppercase text-neutral-600 hover:text-neutral-800"><ArrowLeft className="w-3 h-3" /> All Projects</Link>

      {/* Banner */}
      <div className="panel p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-orange-500/10 blur-3xl" />
        <div className="absolute inset-0 grid-lines opacity-20" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-6 flex-wrap">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500/40 to-red-500/30 border border-orange-500/30 grid place-items-center">
              <ClipboardList className="w-8 h-8" />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase text-neutral-500">{project.project_id} · {project.project_type}</div>
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-1">{project.project_name}</h1>
              <div className="text-neutral-600 text-sm mt-1 max-w-2xl">{project.description || "No description provided."}</div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${PROJECT_STATUS_COLORS[project.status] || ""}`}>{project.status}</span>
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${PRIORITY_COLORS[project.priority] || ""}`}>{project.priority} priority</span>
                {client && <Link to={`/clients/${client.id}`} className="text-[10px] font-mono px-2 py-0.5 rounded border border-[#E4E7EC] bg-[#FFFFFF] text-neutral-700 hover:text-orange-700">Client: {client.client_name}</Link>}
              </div>
            </div>
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="border-[#E4E7EC]" onClick={() => setEditOpen(true)} data-testid="edit-project-btn">
                <PencilLine className="w-4 h-4 mr-1" /> Edit
              </Button>
              {isAdmin && (
                <Button variant="ghost" size="sm" className="text-rose-400 hover:text-rose-700" onClick={remove} data-testid="delete-project-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid md:grid-cols-4 gap-4">
        <Stat icon={LayoutDashboard} label="Current Stage" value={project.current_stage || "—"} testid="stat-stage" />
        <Stat icon={ListChecks} label="Tasks" value={`${stats.tasks_completed}/${stats.tasks_total}`} sub={`${pct}% done`} testid="stat-tasks" />
        <Stat icon={ClipboardList} label="Requirements" value={`${stats.requirements_approved}/${stats.requirements_total}`} sub="approved" testid="stat-reqs" />
        {canSeeCosts(user) && <Stat icon={DollarSign} label="Budget" value={formatMoney(project.estimated_budget, project.currency)} sub={`spent ${formatMoney(project.actual_cost, project.currency)}`} testid="stat-budget" />}
      </div>

      {/* Workflow Stages */}
      <div className="panel p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-orange-400">Pipeline</div>
            <div className="text-sm text-neutral-600">Workflow Stage {currentIdx + 1} of {stages.length}</div>
          </div>
          {canEdit && (
            <Button variant="outline" size="sm" className="border-[#E4E7EC]" onClick={() => setWorkflowOpen(true)} data-testid="edit-workflow-btn">
              <PencilLine className="w-3.5 h-3.5 mr-1" /> Customise Pipeline
            </Button>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {stages.map((s, i) => {
            const done = i < currentIdx;
            const current = i === currentIdx;
            return (
              <button
                key={s}
                disabled={!canEdit}
                onClick={() => canEdit && advanceStage(s)}
                data-testid={`stage-${i}`}
                className={`text-[10px] font-mono px-2.5 py-1.5 rounded-lg border flex items-center gap-1.5 transition-colors ${
                  current ? "bg-orange-500/15 border-orange-500/40 text-orange-700 font-semibold" :
                  done ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-700" :
                  "bg-[#FFFFFF] border-[#E4E7EC] text-neutral-600"
                } ${canEdit ? "hover:border-orange-500/40 cursor-pointer" : "cursor-default"}`}>
                {done && <Check className="w-3 h-3" />}
                {s}
                {i < stages.length - 1 && <ChevronRight className="w-3 h-3 opacity-40 ml-1" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Meta panel */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="panel p-5 space-y-3">
          <div className="text-[10px] font-mono uppercase text-neutral-500 flex items-center gap-1"><Users2 className="w-3 h-3" /> Team</div>
          <div>
            <div className="text-[10px] font-mono text-neutral-500">Project Manager</div>
            <div className="text-sm">{nameOf(project.project_manager_id)}</div>
          </div>
          <div>
            <div className="text-[10px] font-mono text-neutral-500">Dev Team ({project.development_team?.length || 0})</div>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {(project.development_team || []).length === 0 && <span className="text-xs text-neutral-500">None assigned</span>}
              {(project.development_team || []).map(uid => (
                <span key={uid} className="text-[10px] font-mono px-2 py-0.5 rounded border border-[#E4E7EC] bg-[#FFFFFF]" data-testid={`dev-${uid}`}>{nameOf(uid)}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="panel p-5 space-y-3">
          <div className="text-[10px] font-mono uppercase text-neutral-500 flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Timeline</div>
          <div className="grid grid-cols-2 gap-3 text-[11px] font-mono">
            <div><div className="text-neutral-500">Start</div><div className="text-neutral-800">{project.start_date || "—"}</div></div>
            <div><div className="text-neutral-500">Due</div><div className="text-neutral-800">{project.expected_completion_date || "—"}</div></div>
          </div>
          <div className="h-1.5 rounded-full bg-[#E4E7EC] overflow-hidden">
            <div className="h-full bg-gradient-to-r from-orange-500 to-red-500" style={{ width: `${pct}%` }} />
          </div>
          <div className="text-[10px] font-mono text-neutral-500">{pct}% task completion</div>
        </div>

        <div className="panel p-5 space-y-3">
          <div className="text-[10px] font-mono uppercase text-neutral-500 flex items-center gap-1"><Paperclip className="w-3 h-3" /> Attachments & Notes</div>
          {(project.attachments || []).length === 0 && !project.notes && <div className="text-xs text-neutral-500">None</div>}
          {(project.attachments || []).map((a, i) => (
            <a key={i} href={fileUrl(a.path)} target="_blank" rel="noreferrer" className="text-[11px] font-mono flex items-center gap-2 text-orange-700 hover:text-orange-700 truncate" data-testid={`proj-att-${i}`}>
              <Paperclip className="w-3 h-3" /> {a.filename}
            </a>
          ))}
          {project.notes && <div className="text-xs text-neutral-700 whitespace-pre-wrap">{project.notes}</div>}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={searchParams.get("tab") || "requirements"} onValueChange={(v) => setSearchParams({ tab: v }, { replace: true })} className="space-y-4">
        <TabsList className="bg-[#FFFFFF] border border-[#E4E7EC] max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="requirements" data-testid="tab-requirements">Requirements ({requirements.length})</TabsTrigger>
          {!isClient && <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks ({tasks.length})</TabsTrigger>}
          {!isClient && <TabsTrigger value="kanban" data-testid="tab-kanban">Kanban</TabsTrigger>}
          <TabsTrigger value="milestones" data-testid="tab-milestones">Milestones ({milestones.length})</TabsTrigger>
          <TabsTrigger value="bugs" data-testid="tab-bugs">Bugs</TabsTrigger>
          {!isClient && <TabsTrigger value="standup" data-testid="tab-standup">Standup</TabsTrigger>}
          {canSeeCosts(user) && <TabsTrigger value="cost" data-testid="tab-cost">Team Cost</TabsTrigger>}
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
        </TabsList>
        <TabsContent value="requirements">
          <RequirementsPanel project={project} requirements={requirements} users={users} canEdit={canEdit} isClient={isClient} onChanged={load} />
        </TabsContent>
        {!isClient && (
          <TabsContent value="tasks">
            <TasksPanel project={project} tasks={tasks} users={users} canEdit={canEditTasks} currentUser={user} onChanged={load} />
          </TabsContent>
        )}
        {!isClient && (
          <TabsContent value="kanban">
            <KanbanBoard tasks={tasks} users={users} canEdit={canEditTasks} currentUser={user} onChanged={load} project={project} />
          </TabsContent>
        )}
        <TabsContent value="milestones">
          <MilestonesPanel projectId={id} canEdit={canEdit} onChanged={load} />
        </TabsContent>
        <TabsContent value="bugs">
          <BugsPanel projectId={id} users={users} milestones={milestones} canEdit={canEdit} currentUser={user} onChanged={load} />
        </TabsContent>
        {!isClient && (
          <TabsContent value="standup">
            <StandupPanel projectId={id} currentUser={user} canPost={["developer", "tester", "project_manager"].includes(user?.role)} />
          </TabsContent>
        )}
        {canSeeCosts(user) && (
          <TabsContent value="cost">
            <ProjectCostPanel projectId={id} key={activityKey} />
          </TabsContent>
        )}
        <TabsContent value="activity">
          <ActivityLog projectId={id} key={activityKey} />
        </TabsContent>
      </Tabs>

      <ProjectForm open={editOpen} onOpenChange={setEditOpen} initial={project} clients={clients} users={users} onSaved={load} />
      <WorkflowEditor open={workflowOpen} onOpenChange={setWorkflowOpen} project={project} onSaved={load} />
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="panel p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase text-neutral-500">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-xl font-bold mt-1 truncate">{value}</div>
      {sub && <div className="text-[10px] font-mono text-neutral-500 mt-0.5">{sub}</div>}
    </div>
  );
}
